import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';

import { setModal } from 'modules/_shared/redux/modalActions';
import CompleteOperationModal from 'modules/incentivesMarket/modals/CompleteOperationModal';
import { CompleteOperationModalProvider } from 'modules/incentivesMarket/modals/CompleteOperationModal/context/CompleteOperationModalContext';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import menuTypes from 'constants/menuTypes';
import {
  IncentiveOffer,
  IncentiveOfferOperation,
  IncentiveOfferOperationStatus,
} from 'modules/incentivesMarket/types';

import {
  IncentiveOfferOperationDetailContextType,
  OperationTimelineStep,
  OperationPartyInfo,
  OperationDocumentRole,
} from '../types/IncentiveOfferOperationDetailContextType';
import { incentiveOfferOperationDetailInitValue } from '../constants/IncentiveOfferOperationDetailInitValue';
import {
  getIncentiveOfferOperation,
  updateIncentiveOfferOperation,
  postIncentiveOfferOperationComment,
  deleteIncentiveOfferOperationComment,
} from '../../../redux/incentiveOfferOperationActions';
import { getIncentiveOffer } from '../../../redux/incentiveOfferActions';
import {
  enabledOperationStatusOrder,
  timelineStepConfig,
} from '../../../constants/incentiveOfferOperationStatus';
import getOperationReference from '../../../utils/getOperationReference';

const OPERATION_DETAIL_FIELDS_TO_POPULATE = [
  'buyer',
  'seller',
  'unitClass',
  'incentiveOffer',
  'buyerDocuments',
  'sellerDocuments',
  'incentivesMarketPurchaseContract',
  'comments.user',
] as const;

const getTimelineSteps = (
  currentStatus: IncentiveOfferOperationStatus
): OperationTimelineStep[] => {
  const statusIndex = enabledOperationStatusOrder.indexOf(currentStatus);
  const resolvedStatusIndex =
    statusIndex === -1 ? 0 : statusIndex;

  return enabledOperationStatusOrder.map((status, index) => ({
    key: status,
    label: status,
    icon: timelineStepConfig[status]?.icon || 'ni-circle',
    description: timelineStepConfig[status]?.description || '',
    isCompleted:
      (statusIndex !== -1 && index < statusIndex) ||
      currentStatus === IncentiveOfferOperationStatus.COMPLETED,
    isCurrent:
      statusIndex !== -1
        ? index === resolvedStatusIndex
        : index === 0,
  }));
};

export const IncentiveOfferOperationDetailContext =
  createContext<IncentiveOfferOperationDetailContextType>(
    incentiveOfferOperationDetailInitValue
  );

export const IncentiveOfferOperationDetailProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId, operationId } = useParams<{
    societyId: string;
    operationId: string;
  }>();
  const { t } = useTranslate();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const beneficiaryQuery = new URLSearchParams(location.search).get('beneficiaryId');
  const beneficiaryQueryParam = beneficiaryQuery ? `?beneficiaryId=${beneficiaryQuery}` : '';

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );

  const [operation, setOperation] = useState<IncentiveOfferOperation | null>(
    null
  );
  const [incentiveOffer, setIncentiveOffer] = useState<IncentiveOffer | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const operationStatus = useMemo(
    (): IncentiveOfferOperationStatus =>
      operation?.status || IncentiveOfferOperationStatus.PENDING,
    [operation]
  );

  const operationReference = useMemo(
    () => getOperationReference(operation),
    [operation]
  );

  const timelineSteps = useMemo(
    () => getTimelineSteps(operationStatus),
    [operationStatus]
  );

  const totalShares = actualSociety?.sharesCount?.actual || 0;

  const buyerInfo = useMemo((): OperationPartyInfo | null => {
    if (!operation?.buyer || typeof operation.buyer === 'string') return null;
    const { buyer } = operation;
    const sharesBefore = buyer.sharesCount?.actual || 0;
    const sharesAfter = sharesBefore + (operation.unitsCount || 0);
    return {
      beneficiaryId: buyer._id || '',
      name: buyer.name || '',
      cif: buyer.cif || '',
      email: buyer.email || '',
      partnerNumber: buyer.partnerNumber || 0,
      sharesBefore,
      sharesAfter,
      capitalPercentageAfter:
        totalShares > 0 ? (sharesAfter / totalShares) * 100 : 0,
    };
  }, [operation, totalShares]);

  const sellerInfo = useMemo((): OperationPartyInfo | null => {
    if (!operation?.seller || typeof operation.seller === 'string') return null;
    const { seller } = operation;
    const sharesBefore = seller.sharesCount?.actual || 0;
    const sharesAfter = sharesBefore - (operation.unitsCount || 0);
    const isHidden = incentiveOffer?.isSellerNameHidden === true;
    return {
      beneficiaryId: seller._id || '',
      name: isHidden ? t('HiddenName') : seller.name || '',
      cif: isHidden ? '' : seller.cif || '',
      email: isHidden ? t('HiddenEmail') : seller.email || '',
      partnerNumber: seller.partnerNumber || 0,
      sharesBefore,
      sharesAfter,
      capitalPercentageAfter:
        totalShares > 0 ? (sharesAfter / totalShares) * 100 : 0,
    };
  }, [operation, totalShares, incentiveOffer?.isSellerNameHidden, t]);

  const userDocumentRole = useMemo((): OperationDocumentRole | null => {
    if (!beneficiaryQuery || !operation) return null;
    const buyerId =
      operation.buyer && typeof operation.buyer !== 'string'
        ? operation.buyer._id
        : '';
    const sellerId =
      operation.seller && typeof operation.seller !== 'string'
        ? operation.seller._id
        : '';
    if (beneficiaryQuery === buyerId) return 'buyer';
    if (beneficiaryQuery === sellerId) return 'seller';
    return null;
  }, [beneficiaryQuery, operation]);

  const canPostComment = useMemo(() => {
    if (isAdmin) return true;
    if (!operation || !user?.partners?.length) return false;
    const buyerId =
      operation.buyer && typeof operation.buyer !== 'string'
        ? operation.buyer._id
        : operation.buyer || '';
    const sellerId =
      operation.seller && typeof operation.seller !== 'string'
        ? operation.seller._id
        : operation.seller || '';
    const pids = user.partners.map((p) => String(p));
    const b = buyerId ? pids.includes(String(buyerId)) : false;
    const s = sellerId ? pids.includes(String(sellerId)) : false;
    return b || s;
  }, [isAdmin, operation, user]);

  const handleGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleGoToOffer = useCallback(() => {
    if (!incentiveOffer) return;
    history.push(
      `/detalle-oferta-incentivos/${societyId}/${incentiveOffer._id}${beneficiaryQueryParam}`
    );
  }, [history, societyId, incentiveOffer, beneficiaryQueryParam]);

  const handleRefreshOperation = useCallback(async () => {
    if (!societyId || !operationId || !actualSociety) return;

    try {
      const operationData = await (dispatch as any)(
        getIncentiveOfferOperation(operationId, {
          fieldsToPopulate: [...OPERATION_DETAIL_FIELDS_TO_POPULATE],
        })
      );
      if (operationData) {
        setOperation(operationData);

        const offerId =
          typeof operationData.incentiveOffer === 'string'
            ? operationData.incentiveOffer
            : operationData.incentiveOffer?._id;

        if (offerId) {
          const offerData = await (dispatch as any)(
            getIncentiveOffer(offerId, { fieldsToPopulate: ['documents'] })
          );
          if (offerData) {
            setIncentiveOffer(offerData);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing operation:', error);
    }
  }, [societyId, operationId, actualSociety, dispatch]);

  const handleUpdateStatus = useCallback(
    async (status: IncentiveOfferOperationStatus, comment?: string) => {
      if (!operation) return;

      if (status === IncentiveOfferOperationStatus.VALIDATION) {
        dispatch(
          setModal(
            <CompleteOperationModalProvider
              operation={operation}
              onSuccess={handleRefreshOperation}
            >
              <CompleteOperationModal />
            </CompleteOperationModalProvider>
          )
        );
        return;
      }

      const result = await (dispatch as any)(
        updateIncentiveOfferOperation(operation._id, {
          status,
          statusComment: comment,
          updatedBy: user?.['_id'],
        } as any)
      );

      if (result) {
        setOperation(result);
      }
    },
    [operation, dispatch, user, handleRefreshOperation]
  );

  const handleAddComment = useCallback(
    async (text: string) => {
      if (!operation?._id) return;
      const asBeneficiaryId =
        isAdmin && userDocumentRole && beneficiaryQuery ? beneficiaryQuery : undefined;
      const result = await (dispatch as any)(
        postIncentiveOfferOperationComment(operation._id, {
          text,
          ...(asBeneficiaryId ? { asBeneficiaryId } : {}),
        })
      );
      if (result) {
        setOperation(result);
      }
    },
    [operation, dispatch, isAdmin, userDocumentRole, beneficiaryQuery]
  );

  const handleRemoveComment = useCallback(
    async (commentId: string) => {
      if (!operation?._id) return;
      const result = await (dispatch as any)(
        deleteIncentiveOfferOperationComment(operation._id, commentId)
      );
      if (result) {
        setOperation(result);
      }
    },
    [operation, dispatch]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!societyId || !operationId) return;

      setIsLoading(true);

      try {
        const operationData = await (dispatch as any)(
          getIncentiveOfferOperation(operationId, {
            fieldsToPopulate: [...OPERATION_DETAIL_FIELDS_TO_POPULATE],
          })
        );

        if (operationData) {
          setOperation(operationData);

          const offerId =
            typeof operationData.incentiveOffer === 'string'
              ? operationData.incentiveOffer
              : operationData.incentiveOffer?._id;

          if (offerId) {
            const offerData = await (dispatch as any)(
              getIncentiveOffer(offerId, { fieldsToPopulate: ['documents'] })
            );
            if (offerData) {
              setIncentiveOffer(offerData);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching trading offer operation detail:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (actualSociety) {
      fetchData();
    }
  }, [societyId, operationId, actualSociety, dispatch]);

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        operation,
        incentiveOffer,
        operationReference,
        operationStatus,
        timelineSteps,
        buyerInfo,
        sellerInfo,
        isAdmin,
        isLoading: isLoading || !actualSociety,
        beneficiaryId: beneficiaryQuery || '',
        userDocumentRole,
        canPostComment,
      },
      actions: {
        handleGoBack,
        handleUpdateStatus,
        handleGoToOffer,
        handleRefreshOperation,
        handleAddComment,
        handleRemoveComment,
      },
    }),
    [
      societyId,
      actualSociety,
      operation,
      incentiveOffer,
      operationReference,
      operationStatus,
      timelineSteps,
      buyerInfo,
      sellerInfo,
      isAdmin,
      isLoading,
      beneficiaryQuery,
      userDocumentRole,
      canPostComment,
      handleGoBack,
      handleUpdateStatus,
      handleGoToOffer,
      handleRefreshOperation,
      handleAddComment,
      handleRemoveComment,
    ]
  );

  return (
    <IncentiveOfferOperationDetailContext.Provider value={contextValue}>
      {children}
    </IncentiveOfferOperationDetailContext.Provider>
  );
};

export const useIncentiveOfferOperationDetail = () => {
  const context = useContext(IncentiveOfferOperationDetailContext);
  if (!context) {
    throw new Error(
      'useIncentiveOfferOperationDetail must be used within a IncentiveOfferOperationDetailProvider'
    );
  }
  return context;
};
